import { useState } from 'react'
import { AxiosError } from 'axios'
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material'
import { PageShell } from '@components/common/PageShell'
import type { ScoringOptionsDto } from '@api/types/scoring'
import {
  useResetScoringOptions,
  useScoringOptions,
  useUpdateScoringOptions,
} from '../hooks/useScoringOptions'
import { RankLadderForm, RecomputePanel, ScoringOptionsForm } from '../components/scoring'

export default function AdminScoringPage() {
  const { data, isLoading, error } = useScoringOptions()
  const update = useUpdateScoringOptions()
  const reset = useResetScoringOptions()

  // Saving doesn't move the ladders; track that a recompute is owed until one is run.
  const [recomputePending, setRecomputePending] = useState(false)

  const handleSubmit = (values: ScoringOptionsDto) => {
    update.mutate(values, {
      onSuccess: (response) => setRecomputePending(response.requiresRecompute),
    })
  }

  const handleReset = () => {
    reset.mutate(undefined, {
      onSuccess: (response) => setRecomputePending(response.requiresRecompute),
    })
  }

  // The early returns keep <PageShell> so the AppBar title is right while the page is still
  // loading or has failed — PageShell is what sets it.
  if (isLoading) {
    return (
      <PageShell title="Leaderboard scoring">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    )
  }

  if (error || !data) {
    return (
      <PageShell title="Leaderboard scoring">
        <Alert severity="error">
          Failed to load scoring options. {(error as Error)?.message}
        </Alert>
      </PageShell>
    )
  }

  const saveError = update.error as AxiosError<{ message?: string }> | null
  const serverError = saveError?.response?.data?.message ?? (saveError ? 'Failed to save.' : null)

  return (
    <PageShell
      title="Leaderboard scoring"
      subtitle="Tune how completions convert into leaderboard points."
    >
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>What changing these does — and doesn&apos;t — do</AlertTitle>
        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
          <li>
            <strong>Saving does not move any ladder.</strong> Standings are precomputed, so new values
            only take effect when the recompute next runs.
          </li>
          <li>
            <strong>Saving does not affect achievements or badges.</strong> Their eligibility gate reads
            none of these knobs — it only checks that an adventure is public, published, approved, unflagged,
            and not completed by its own creator.
          </li>
          <li>
            A knob left at its default stores nothing, so it keeps tracking future changes to the shipped
            defaults. &ldquo;Reset all&rdquo; removes the stored values rather than freezing today&apos;s in place.
          </li>
        </ul>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <ScoringOptionsForm
            data={data}
            onSubmit={handleSubmit}
            onReset={handleReset}
            isSaving={update.isPending}
            isResetting={reset.isPending}
            serverError={serverError}
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <RecomputePanel recomputePending={recomputePending} />

          {/* The other ladder. Same page because an admin tuning one almost always wants to see
              the other: points earned from achievements feed the tiers, scores feed the boards. */}
          <Box sx={{ mt: 3 }}>
            <RankLadderForm />
          </Box>

          {data.overrides.length > 0 && (
            <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Scoring knobs changed from the defaults
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {data.overrides.length} knob{data.overrides.length === 1 ? '' : 's'} changed from the defaults.
              </Typography>
              {data.overrides.map((override) => (
                <Box key={override.key} sx={{ mb: 1.5 }}>
                  <Typography variant="body2">
                    {override.key.replace('Scoring.', '')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(override.updatedAt).toLocaleString()}
                    {override.updatedByDisplayName ? ` · ${override.updatedByDisplayName}` : ''}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Grid>
      </Grid>
    </PageShell>
  )
}
